/**
 * Rich per-engine classification of connection/query errors for analytics
 * (`connection:error`, `feature:query_error`) and for actionable UI hints.
 *
 * Input is the flattened error message the renderer already has, plus — when
 * an engine preserved it across IPC — the structured driver code. Output is a
 * low-cardinality category (see `AnalyticsErrorCategory`) and a driver error
 * code (MSSQL ELOGIN/ESOCKET/number, PG SQLSTATE, MySQL errno, Oracle
 * ORA-NNNNN, SQLite SQLITE_*). NEVER returns raw text, hostnames, or any
 * other user-identifying data.
 */
import { isCertificateTrustError } from '../../features/connections/utils/certificateTrustError';
import { categorizeError, type AnalyticsErrorCategory } from './analyticsTypes';
import { SHARED_SIGNATURES, signaturesForEngine, oracleCodeCategory } from './dbErrorSignatures';

export type DbErrorClassification = {
  category: AnalyticsErrorCategory;
  /** Low-cardinality driver error code; absent when nothing safe was found. */
  code?: string;
};

/**
 * Keep the `code` event property strictly low-cardinality: short
 * identifier-shaped tokens only, never free text.
 */
export function sanitizeDbErrorCode(code: string | number | undefined | null): string | undefined {
  if (code === undefined || code === null) return undefined;
  const text = String(code).trim().toUpperCase();
  // No known driver code vocabulary contains a dot (SQLSTATE is alnum,
  // ER_*/SQLITE_*/HANDSHAKE_* use underscores, ORA-/NJS- use dashes, tedious
  // codes are bare letters) — rejecting dots keeps hostname/deployment-shaped
  // tokens out. Max 36 chars fits the longest known node TLS code
  // (UNABLE_TO_GET_ISSUER_CERT_LOCALLY, 33).
  return /^[A-Z0-9][A-Z0-9_-]{0,35}$/.test(text) ? text : undefined;
}

/** Node/driver codes that appear verbatim inside flattened messages. */
const KNOWN_MESSAGE_CODES = [
  'ELOGIN', 'EENCRYPT', 'EINSTLOOKUP', 'ETIMEOUT', 'ETIMEDOUT', 'ESOCKET',
  'ECONNREFUSED', 'ECONNRESET', 'ECONNABORTED', 'ENOTFOUND', 'EHOSTUNREACH',
  'ENETUNREACH', 'EHOSTDOWN', 'EACCES', 'EPERM', 'EPIPE', 'EAI_AGAIN', 'ENOENT',
] as const;

/** Best-effort code recovery from a flattened message (allowlisted shapes only). */
function extractCodeFromMessage(engine: string | undefined, message: string): string | undefined {
  const ora = message.match(/\b(ORA|NJS)-(\d{1,5})\b/i);
  if (ora) {
    const width = ora[1].toUpperCase() === 'ORA' ? 5 : 3;
    return `${ora[1].toUpperCase()}-${ora[2].padStart(width, '0')}`;
  }
  const sqlite = message.match(/\bSQLITE_[A-Z_]{2,30}\b/);
  if (sqlite) return sqlite[0];
  const mysqlSymbol = message.match(/\bER_[A-Z_]{2,40}\b/);
  if (mysqlSymbol) return mysqlSymbol[0];
  // mssqlDriverAdapter decorates query errors as "Error 208: …"
  if (engine === 'mssql') {
    const mssqlNumber = message.match(/\bError (\d{1,5}):/);
    if (mssqlNumber) return mssqlNumber[1];
  }
  const nodeCode = KNOWN_MESSAGE_CODES.find((code) =>
    new RegExp(`\\b${code}\\b`).test(message)
  );
  return nodeCode;
}

const NODE_CODE_CATEGORIES: Record<string, AnalyticsErrorCategory> = {
  ELOGIN: 'auth_failed',
  EENCRYPT: 'tls_required',
  EINSTLOOKUP: 'instance_not_found',
  ENOTFOUND: 'dns_not_found',
  EAI_AGAIN: 'dns_not_found',
  ECONNREFUSED: 'conn_refused',
  EHOSTUNREACH: 'host_unreachable',
  ENETUNREACH: 'host_unreachable',
  EHOSTDOWN: 'host_unreachable',
  ETIMEOUT: 'timeout',
  ETIMEDOUT: 'timeout',
  ECONNRESET: 'connection_lost',
  ECONNABORTED: 'connection_lost',
  EPIPE: 'connection_lost',
  EACCES: 'permission',
  EPERM: 'permission',
  ENOENT: 'file_not_found',
  // Node TLS verification codes (structured only — messages carry the phrases)
  DEPTH_ZERO_SELF_SIGNED_CERT: 'tls_cert_untrusted',
  SELF_SIGNED_CERT_IN_CHAIN: 'tls_cert_untrusted',
  UNABLE_TO_VERIFY_LEAF_SIGNATURE: 'tls_cert_untrusted',
  UNABLE_TO_GET_ISSUER_CERT_LOCALLY: 'tls_cert_untrusted',
  CERT_HAS_EXPIRED: 'tls_cert_untrusted',
  ERR_TLS_CERT_ALTNAME_INVALID: 'tls_cert_untrusted',
};

const MSSQL_NUMBER_CATEGORIES: Record<string, AnalyticsErrorCategory> = {
  '18456': 'auth_failed', '18452': 'auth_failed', '40531': 'auth_failed',
  '4060': 'db_not_found', '40615': 'firewall_blocked',
  '102': 'syntax_error', '105': 'syntax_error', '156': 'syntax_error',
  // 8118/8120/8121/8127: column invalid in select list / HAVING / ORDER BY
  // because it is not in an aggregate or the GROUP BY clause.
  '8118': 'syntax_error', '8120': 'syntax_error',
  '8121': 'syntax_error', '8127': 'syntax_error',
  // 195 "not a recognized built-in function name" is syntax_error, NOT
  // object_not_found: object_not_found drives the "Switch database…" query
  // tip/action, which can never fix a mistyped built-in.
  '195': 'syntax_error',
  '207': 'object_not_found', '208': 'object_not_found', '2812': 'object_not_found',
  '3701': 'object_not_found', '4104': 'object_not_found',
  '515': 'constraint_violation', '547': 'constraint_violation',
  '2601': 'constraint_violation', '2627': 'constraint_violation',
  '2628': 'constraint_violation', '8152': 'constraint_violation',
  // 2714 "there is already an object named X" is a DDL name collision, not a
  // data-integrity constraint — bucketed here as the closest existing
  // category (matches the 2601/2627 duplicate spirit).
  '2714': 'constraint_violation',
  '8102': 'constraint_violation', // cannot update identity column
  '263': 'syntax_error', // "Must specify table to select from."
  '4145': 'syntax_error', // non-boolean expression where a condition is expected
  '206': 'conversion_error', '241': 'conversion_error', '242': 'conversion_error',
  '245': 'conversion_error', '8114': 'conversion_error', '8115': 'conversion_error',
  '1205': 'lock_contention', '1222': 'lock_contention',
  '229': 'permission', '230': 'permission', '297': 'permission', '916': 'permission',
};

const MYSQL_ERRNO_CATEGORIES: Record<string, AnalyticsErrorCategory> = {
  '1045': 'auth_failed', '1251': 'auth_failed', '3159': 'tls_required',
  '1049': 'db_not_found', '1064': 'syntax_error',
  '1146': 'object_not_found', '1054': 'object_not_found',
  '1062': 'constraint_violation', '1451': 'constraint_violation',
  '1452': 'constraint_violation', '1048': 'constraint_violation',
  '1366': 'conversion_error', '1292': 'conversion_error',
  '1142': 'permission', '1044': 'permission',
  '1205': 'lock_contention', '1213': 'lock_contention', '1317': 'cancelled',
};

/** mysql2 symbolic codes double the errno table for the codes we care about. */
const MYSQL_SYMBOL_CATEGORIES: Record<string, AnalyticsErrorCategory> = {
  ER_ACCESS_DENIED_ERROR: 'auth_failed', ER_BAD_DB_ERROR: 'db_not_found',
  ER_SECURE_TRANSPORT_REQUIRED: 'tls_required', ER_PARSE_ERROR: 'syntax_error',
  HANDSHAKE_NO_SSL_SUPPORT: 'tls_unsupported',
  ER_NO_SUCH_TABLE: 'object_not_found', ER_BAD_FIELD_ERROR: 'object_not_found',
  ER_DUP_ENTRY: 'constraint_violation', ER_NO_REFERENCED_ROW_2: 'constraint_violation',
  ER_ROW_IS_REFERENCED_2: 'constraint_violation', ER_BAD_NULL_ERROR: 'constraint_violation',
  ER_TABLEACCESS_DENIED_ERROR: 'permission', ER_DBACCESS_DENIED_ERROR: 'permission',
  ER_LOCK_WAIT_TIMEOUT: 'lock_contention', ER_LOCK_DEADLOCK: 'lock_contention',
  ER_QUERY_INTERRUPTED: 'cancelled', ER_UNKNOWN_SYSTEM_VARIABLE: 'syntax_error',
  // ER_CANT_AGGREGATE_2COLLATIONS ("Illegal mix of collations for operation")
  // is a charset/collation mismatch, not a scalar type conversion —
  // conversion_error's tip ("check literals and formats: dates, numbers,
  // quoting") would send the user looking in the wrong place, so this stays
  // in the closest safe bucket (no tip/action attached to syntax_error).
  ER_CANT_AGGREGATE_2COLLATIONS: 'syntax_error',
};

function pgSqlStateCategory(code: string): AnalyticsErrorCategory | undefined {
  if (!/^\d\d[0-9A-Z]{3}$/.test(code)) return undefined;
  const byCode: Record<string, AnalyticsErrorCategory> = {
    '42601': 'syntax_error', '42501': 'permission',
    '42P01': 'object_not_found', '42703': 'object_not_found',
    '42883': 'object_not_found', '42704': 'object_not_found',
    '3D000': 'db_not_found', '57014': 'cancelled',
    '40P01': 'lock_contention', '40001': 'lock_contention', '55P03': 'lock_contention',
  };
  if (byCode[code]) return byCode[code];
  const byClass: Record<string, AnalyticsErrorCategory> = {
    '28': 'auth_failed', '23': 'constraint_violation',
    '22': 'conversion_error', '08': 'connection_lost',
  };
  return byClass[code.slice(0, 2)];
}

function categoryForCode(engine: string | undefined, code: string): AnalyticsErrorCategory | undefined {
  if (NODE_CODE_CATEGORIES[code]) return NODE_CODE_CATEGORIES[code];
  if (/^(ORA|NJS)-|^JAM_ORACLE_/.test(code)) return oracleCodeCategory(code);
  if (code.startsWith('SQLITE_')) {
    if (/^SQLITE_(CANTOPEN|NOTADB)/.test(code)) return 'file_not_found';
    if (/^SQLITE_(BUSY|LOCKED)/.test(code)) return 'lock_contention';
    if (/^SQLITE_(READONLY|AUTH|PERM)/.test(code)) return 'permission';
    if (code.startsWith('SQLITE_CONSTRAINT')) return 'constraint_violation';
    return undefined;
  }
  if (MYSQL_SYMBOL_CATEGORIES[code]) return MYSQL_SYMBOL_CATEGORIES[code];
  if (engine === 'mssql') return MSSQL_NUMBER_CATEGORIES[code];
  if (engine === 'mysql') return MYSQL_ERRNO_CATEGORIES[code];
  if (engine === 'pgsql') return pgSqlStateCategory(code);
  return undefined;
}

/**
 * Codes that name more than one server condition, so the MESSAGE (when it
 * matches a signature) must be allowed to disambiguate before the code map
 * gets its say: MSSQL wraps "Cannot open database" (4060) as a bare ELOGIN
 * on Azure SQL Edge, and PG uses 57014 for both statement_timeout and a
 * user-requested cancel.
 */
const AMBIGUOUS_CODES = new Set(['ELOGIN', '57014']);

/**
 * Classify a database error for telemetry and hint UIs.
 *
 * Precedence: an authoritative STRUCTURED driver code (from the error
 * object, not the text) beats everything except its own ambiguity; then
 * engine message signatures; then the cert-trust phrase list (free text can
 * quote a cert phrase, so it must not outrank a real non-TLS code); then
 * shared signatures; then codes recovered from the message; then the legacy
 * coarse matcher.
 *
 * @param engine  Canonical engine id (`mssql` | `pgsql` | …) when known.
 * @param message The flattened error message shown to the user.
 * @param code    Structured driver code when it survived IPC (preferred over
 *                anything recovered from the message).
 */
export function classifyDbError(
  engine: string | undefined,
  message: string | undefined | null,
  code?: string | number
): DbErrorClassification {
  const text = message ?? '';
  const structuredCode = sanitizeDbErrorCode(code);
  const resolvedCode = structuredCode ?? extractCodeFromMessage(engine, text);

  if (structuredCode && !AMBIGUOUS_CODES.has(structuredCode)) {
    const byStructuredCode = categoryForCode(engine, structuredCode);
    if (byStructuredCode) return { category: byStructuredCode, code: structuredCode };
  }

  for (const signature of signaturesForEngine(engine)) {
    if (signature.match.test(text)) {
      return { category: signature.category, code: resolvedCode ?? signature.code };
    }
  }

  // Cert distrust hides under generic socket codes (tedious wraps it in
  // ESOCKET), so the shared phrase list still outranks the SHARED network
  // signatures — but no longer an authoritative structured code above.
  if (isCertificateTrustError(text)) {
    return { category: 'tls_cert_untrusted', code: resolvedCode };
  }

  for (const signature of SHARED_SIGNATURES) {
    if (signature.match.test(text)) {
      return { category: signature.category, code: resolvedCode ?? signature.code };
    }
  }

  if (resolvedCode) {
    const byCode = categoryForCode(engine, resolvedCode);
    if (byCode) return { category: byCode, code: resolvedCode };
  }

  // Legacy coarse buckets (auth_failed/timeout/network/ssl/permission/
  // not_found) keep matching what they always matched.
  return { category: categorizeError(text), code: resolvedCode };
}
